import React, { Fragment, useEffect, useRef, useState } from 'react';
import Page, { usePage } from '../../components/Page';
import { Button, Checkbox, FormControlLabel, Grid, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import { SideBar, useSideBar } from '../../components/SideBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api } from '../../api';
import {
    faArrowDown,
    faArrowUp,
    faCheck,
    faExclamation,
    faMagnifyingGlass,
    faPlus,
    faTurnDownRight,
    faUpRight,
} from '@fortawesome/pro-regular-svg-icons';
import { useStyles } from '../../hooks/useStyles';
import { EditCarQueueDialog, useEditCarQueueDialog } from './EditCarQueueDialog';
import { useAuth } from '../../units/auth/AuthProvider';
import carQueue, { CarQueue } from '../../api/car-queue';
import moment from 'moment';
import { washMicroSeconds, washMinutes } from '../../data/WashTime';
import { green, grey, orange, red } from '@mui/material/colors';
import { useScreenSize } from '../../hooks/useScreenSize';
import { PostponeDialog, usePostponeDialog } from './PostponeDialog';
import { AdvanceDialog, useAdvanceDialog } from './AdvanceDialog';
import { User } from '../../api/user';
import { Store } from '../../api/store';
import mqtt, { MqttClient } from 'mqtt';

type DataRow = CarQueue & {
    selected: boolean;
    index: number;
    prevCarQueue: DataRow | undefined;
    nextCarQueue: DataRow | undefined;
    prevSpaces: number;
    nextSpaces: number;
    estimateUnixTime: number;
    warnings: string[];
    users: User[];
};

export const useCarQueueManagePage = () => {
    const page = usePage();
    const classes = useStyles();
    const auth = useAuth();
    const screenSize = useScreenSize();
    const [carQueues, setCarQueues] = useState<CarQueue[]>([]);
    const carQueuesRef = useRef<CarQueue[]>(carQueues);
    useEffect(() => {
        carQueuesRef.current = carQueues;
    }, [carQueues]);
    const [rows, setRows] = useState<DataRow[]>([]);
    const [inputSearch, setInputSearch] = useState('');
    const filteredRows = rows.filter((row) => {
        const searchs = inputSearch.split(/\s+/).filter((v) => v.length > 0);
        return [
            searchs.length == 0
                ? true
                : searchs
                      .map(
                          (s) =>
                              row.carId?.toUpperCase().includes(s.toUpperCase()) ||
                              moment(row.estimateTime).format('YYYY-MM-DD A hh:mm').includes(s.toUpperCase()),
                      )
                      .every((v) => v),
        ].every((v) => v);
    });
    const sideBar = useSideBar();
    const advanceDialog = useAdvanceDialog();
    const postponeDialog = usePostponeDialog();
    const [prevSelectedIndex, setPrevSelectedIndex] = useState<number>(-1);
    const [stores, setStores] = useState<Store[]>([]);
    const [selectedStore, setSelectedStore] = useState<string>('default');
    const selectedStoreRef = useRef(selectedStore);
    useEffect(() => {
        selectedStoreRef.current = selectedStore;
    }, [selectedStore]);
    const [mqttClient, setMqttClient] = useState<MqttClient | null>(null);
    const mqttClientRef = useRef(mqttClient);
    useEffect(() => {
        mqttClientRef.current = mqttClient;
        if (!mqttClient) {
            const mqttClient = mqtt.connect(process.env.REACT_APP_MQTT_BROKER_URL!);
            setMqttClient(mqttClient);
        } else {
            mqttClient.on('connect', () => {
                console.log('Connected to MQTT broker');
            });
            mqttClient.on('error', (err) => {
                console.error('MQTT Client Error:', err);
                mqttClient.end();
            });
            mqttClient.on('reconnect', () => {
                console.log('Reconnecting to MQTT broker');
            });
            mqttClient.on('message', (topic, message) => {
                console.log(`Message received on topic ${topic}:`, message.toString());
                refresh();
            });
            mqttClient.subscribe('database/table/car_queue/#', { qos: 0 }, (error) => {
                if (error) {
                    console.error('Subscribe to topics error:', error);
                    return;
                }
                console.log('Subscribed to topic: database/table/car_queue/#');
            });
        }
        return () => {
            if (mqttClient) {
                mqttClient.end();
                console.log('Disconnected to MQTT broker');
            }
        };
    }, [mqttClient]);
    const getCarQueues = async () => {
        const newCarQueues = await api.carQueue
            .get({
                complete: 'N',
                cancel: 'N',
                estimateTimeSt: new Date(new Date(moment().format('YYYY/MM/DD')).getTime()),
                estimateTimeEd: new Date(new Date(moment().format('YYYY/MM/DD')).getTime() + 24 * 60 * 60 * 1000),
                store: { storeId: selectedStoreRef.current },
                order: { estimateTime: 'asc' },
            })
            .then((ret) => ret.data || []);
        return newCarQueues;
    };
    const reloadCarQueues = async () => {
        const newCarQueues = await getCarQueues();
        setCarQueues(newCarQueues);
    };
    const reloadStores = async () => {
        const newStores = await api.store.get({}).then((ret) => ret.data || []);
        setStores(newStores);
        if (newStores.length == 1) setSelectedStore(newStores[0].storeId!);
    };
    const editCarQueueDialog = useEditCarQueueDialog({ ...page, reloadCarQueues, carQueues });
    useEffect(() => {
        reloadCarQueues();
    }, [selectedStore]);
    const refresh = async () => {
        await reloadStores();
        const newCarQueues = await getCarQueues();
        const oldQueue = carQueuesRef.current
            .map((carQueue) => `${carQueue.carQueueId} ${carQueue.carId} ${carQueue.estimateTime} ${carQueue.complete} ${carQueue.cancel}`)
            .join(', ');
        const newQueue = newCarQueues
            .map((carQueue) => `${carQueue.carQueueId} ${carQueue.carId} ${carQueue.estimateTime} ${carQueue.complete} ${carQueue.cancel}`)
            .join(', ');
        if (oldQueue != newQueue) {
            setCarQueues(newCarQueues);
            page.simpleAlert.show({ autoHideDuration: 2000, severity: 'warning', message: '有資料更新' });
        } else {
            // page.simpleAlert.show({ autoHideDuration: 2000, severity: 'info', message: '沒資料更新' });
        }
    };
    useEffect(() => {
        reloadStores();
        reloadCarQueues();
        const interval = setInterval(() => {
            if (mqttClientRef.current == null || !mqttClientRef.current.connected) refresh();
        }, 10 * 1000);
        return () => clearInterval(interval);
    }, []);
    useEffect(() => {
        (async () => {
            let newRows: DataRow[] = await Promise.all(
                carQueues.map(async (carQueue, idx) => {
                    const users = (
                        await api.car
                            .get({ carId: carQueue.carId, relations: { users: true } })
                            .then((ret) => (ret.data || []).map((car) => car.users || []))
                    ).flat();
                    return {
                        ...carQueue,
                        selected: false,
                        index: idx,
                        prevCarQueue: undefined,
                        nextCarQueue: undefined,
                        prevSpaces: 0,
                        nextSpaces: 0,
                        estimateUnixTime: new Date(carQueue.estimateTime!).getTime(),
                        warnings: [],
                        users: users,
                    };
                }),
            );
            newRows = newRows.map((row, idx, arr) => {
                return {
                    ...row,
                    prevCarQueue: idx > 0 ? { ...arr[idx - 1] } : undefined,
                    nextCarQueue: idx < arr.length - 1 ? arr[idx + 1] : undefined,
                };
            });
            newRows = newRows.map((row, idx, arr) => {
                if (row.prevCarQueue && row.estimateUnixTime - row.prevCarQueue.estimateUnixTime < washMicroSeconds)
                    row.warnings.push(`距離前車時間小於${washMinutes}分鐘`);
                if (row.nextCarQueue && row.nextCarQueue.estimateUnixTime - row.estimateUnixTime < washMicroSeconds)
                    row.warnings.push(`距離後車時間小於${washMinutes}分鐘`);
                const repeatTimes = arr.filter((v) => v.carId == row.carId).length;
                repeatTimes > 1 && row.warnings.push(`隊伍中車號 ${row.carId} 重複出現 ${repeatTimes} 次`);
                row.prevSpaces = row.prevCarQueue
                    ? Math.min(
                          9,
                          Math.floor((row.estimateUnixTime - row.prevCarQueue.estimateUnixTime - washMicroSeconds) / washMicroSeconds),
                      )
                    : 9;
                row.nextSpaces = row.nextCarQueue
                    ? Math.min(
                          9,
                          Math.floor((row.nextCarQueue.estimateUnixTime - row.estimateUnixTime - washMicroSeconds) / washMicroSeconds),
                      )
                    : 9;
                return { ...row };
            });
            setRows(newRows);
        })();
    }, [carQueues]);
    return {
        page,
        classes,
        auth,
        screenSize,
        carQueues,
        setCarQueues,
        rows,
        setRows,
        inputSearch,
        setInputSearch,
        filteredRows,
        reloadCarQueues,
        sideBar,
        editCarQueueDialog,
        advanceDialog,
        postponeDialog,
        prevSelectedIndex,
        setPrevSelectedIndex,
        stores,
        setStores,
        selectedStore,
        setSelectedStore,
    };
};

export const CarQueueManagePage = (): React.ReactElement => {
    const props = useCarQueueManagePage();
    const rowStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderCollapse: 'collapse',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        height: 44,
    };
    const hintStyle: React.CSSProperties = {
        position: 'absolute',
        right: -10,
        top: -10,
        width: 10 * 2,
        borderRadius: 10,
        fontSize: 10,
        fontFamily: 'verdana',
        backgroundColor: orange[600],
        color: 'white',
    };
    return (
        <Page {...props.page} title='排序管理'>
            <Grid container flexDirection={'row'} height={'100%'} overflow={'hidden'} sx={{ userSelect: 'none' }}>
                <Grid
                    container
                    item
                    flex={1}
                    flexDirection={'column'}
                    padding={props.screenSize.mdUp ? 4 : 1}
                    height={'100%'}
                    overflow={'hidden'}
                    sx={{ backgroundColor: grey[100] }}
                >
                    <Grid container item flexDirection={'row'}>
                        <Grid container item flexDirection={'row'} justifyContent={'center'} md={3} xs={6}>
                            <Select
                                disabled={props.rows.filter((r) => r.selected).length == 0}
                                value={'default'}
                                onChange={(e) => {}}
                                fullWidth
                                style={{ margin: 4, marginTop: 12, backgroundColor: 'white' }}
                                defaultValue={'default'}
                            >
                                <MenuItem value={'default'} disabled>
                                    {'已選擇項目：'}
                                </MenuItem>
                                <MenuItem
                                    tabIndex={-1}
                                    value={'completeBulk'}
                                    onClick={() => {
                                        props.page.alert.show({
                                            type: 'warning',
                                            title: '警告',
                                            message: '確定要批次完成？',
                                            onConfirm: async () => {
                                                props.page.setIsLoading(true);
                                                const ret = await api.carQueue
                                                    .updateBulk(
                                                        props.rows
                                                            .filter((r) => r.selected)
                                                            .map((r) => ({ ...r, carQueueId: r.carQueueId!, complete: 'Y' })),
                                                    )
                                                    .finally(() => props.page.setIsLoading(false));
                                                if (ret.success) {
                                                    props.reloadCarQueues();
                                                    props.page.simpleAlert.show({
                                                        autoHideDuration: 2000,
                                                        severity: 'success',
                                                        message: ret.message,
                                                    });
                                                } else {
                                                    props.page.simpleAlert.show({
                                                        autoHideDuration: 6000,
                                                        severity: 'error',
                                                        message: JSON.stringify(ret),
                                                    });
                                                }
                                            },
                                        });
                                    }}
                                >
                                    {'完成'}
                                </MenuItem>
                                <MenuItem
                                    tabIndex={-1}
                                    value={'cancelBulk'}
                                    onClick={() => {
                                        props.page.alert.show({
                                            type: 'warning',
                                            title: '警告',
                                            message: '確定要批次取消？',
                                            onConfirm: async () => {
                                                props.page.setIsLoading(true);
                                                const ret = await api.carQueue
                                                    .updateBulk(
                                                        props.rows
                                                            .filter((r) => r.selected)
                                                            .map((r) => ({ ...r, carQueueId: r.carQueueId!, cancel: 'Y' })),
                                                    )
                                                    .finally(() => props.page.setIsLoading(false));
                                                if (ret.success) {
                                                    props.reloadCarQueues();
                                                    props.page.simpleAlert.show({
                                                        autoHideDuration: 2000,
                                                        severity: 'success',
                                                        message: ret.message,
                                                    });
                                                } else {
                                                    props.page.simpleAlert.show({
                                                        autoHideDuration: 6000,
                                                        severity: 'error',
                                                        message: JSON.stringify(ret),
                                                    });
                                                }
                                            },
                                        });
                                    }}
                                >
                                    {'取消'}
                                </MenuItem>
                                <MenuItem
                                    tabIndex={-1}
                                    value={'deleteBulk'}
                                    onClick={() => {
                                        props.page.alert.show({
                                            type: 'warning',
                                            title: '警告',
                                            message: '確定要批次刪除？',
                                            onConfirm: async () => {
                                                props.page.setIsLoading(true);
                                                const ret = await api.carQueue
                                                    .removeBulk(
                                                        props.rows
                                                            .filter((r) => r.selected)
                                                            .map((r) => ({ ...r, carQueueId: r.carQueueId! })),
                                                    )
                                                    .finally(() => props.page.setIsLoading(false));
                                                if (ret.success) {
                                                    props.reloadCarQueues();
                                                    props.page.simpleAlert.show({
                                                        autoHideDuration: 2000,
                                                        severity: 'success',
                                                        message: ret.message,
                                                    });
                                                } else {
                                                    props.page.simpleAlert.show({
                                                        autoHideDuration: 6000,
                                                        severity: 'error',
                                                        message: JSON.stringify(ret),
                                                    });
                                                }
                                            },
                                        });
                                    }}
                                >
                                    {'刪除'}
                                </MenuItem>
                                <MenuItem
                                    tabIndex={-1}
                                    value={'advanceBulk'}
                                    onClick={() => {
                                        props.advanceDialog.setOnConfirm(() => (advanceMinutes: number) => {
                                            props.page.alert.show({
                                                type: 'warning',
                                                title: '警告',
                                                message: `確定要批次提前${advanceMinutes}分鐘？`,
                                                onConfirm: async () => {
                                                    props.page.setIsLoading(true);
                                                    const ret = await api.carQueue
                                                        .updateBulk(
                                                            props.rows
                                                                .filter((r) => r.selected)
                                                                .map((r) => ({
                                                                    ...r,
                                                                    carQueueId: r.carQueueId!,
                                                                    estimateTime: r.estimateTime
                                                                        ? new Date(
                                                                              new Date(r.estimateTime).getTime() -
                                                                                  advanceMinutes * 60 * 1000,
                                                                          )
                                                                        : undefined,
                                                                })),
                                                        )
                                                        .finally(() => {
                                                            props.page.setIsLoading(false);
                                                            props.advanceDialog.onClose();
                                                        });
                                                    if (ret.success) {
                                                        props.reloadCarQueues();
                                                        props.page.simpleAlert.show({
                                                            autoHideDuration: 2000,
                                                            severity: 'success',
                                                            message: ret.message,
                                                        });
                                                    } else {
                                                        props.page.simpleAlert.show({
                                                            autoHideDuration: 6000,
                                                            severity: 'error',
                                                            message: JSON.stringify(ret),
                                                        });
                                                    }
                                                },
                                            });
                                        });
                                        props.advanceDialog.onOpen();
                                    }}
                                >
                                    {'提前'}
                                </MenuItem>
                                <MenuItem
                                    tabIndex={-1}
                                    value={'postponeBulk'}
                                    onClick={() => {
                                        props.postponeDialog.setOnConfirm(() => (postponeMinutes: number) => {
                                            props.page.alert.show({
                                                type: 'warning',
                                                title: '警告',
                                                message: `確定要批次延後${postponeMinutes}分鐘？`,
                                                onConfirm: async () => {
                                                    props.page.setIsLoading(true);
                                                    const ret = await api.carQueue
                                                        .updateBulk(
                                                            props.rows
                                                                .filter((r) => r.selected)
                                                                .map((r) => ({
                                                                    ...r,
                                                                    carQueueId: r.carQueueId!,
                                                                    estimateTime: r.estimateTime
                                                                        ? new Date(
                                                                              new Date(r.estimateTime).getTime() +
                                                                                  postponeMinutes * 60 * 1000,
                                                                          )
                                                                        : undefined,
                                                                })),
                                                        )
                                                        .finally(() => {
                                                            props.page.setIsLoading(false);
                                                            props.postponeDialog.onClose();
                                                        });
                                                    if (ret.success) {
                                                        props.reloadCarQueues();
                                                        props.page.simpleAlert.show({
                                                            autoHideDuration: 2000,
                                                            severity: 'success',
                                                            message: ret.message,
                                                        });
                                                    } else {
                                                        props.page.simpleAlert.show({
                                                            autoHideDuration: 6000,
                                                            severity: 'error',
                                                            message: JSON.stringify(ret),
                                                        });
                                                    }
                                                },
                                            });
                                        });
                                        props.postponeDialog.onOpen();
                                    }}
                                >
                                    {'順延'}
                                </MenuItem>
                            </Select>
                        </Grid>
                        <Grid container item flexDirection={'row'} justifyContent={'center'} md={3} xs={6}>
                            <Select
                                value={props.selectedStore}
                                onChange={(e) => props.setSelectedStore(e.target.value)}
                                fullWidth
                                style={{ margin: 4, marginTop: 12, backgroundColor: 'white' }}
                            >
                                <MenuItem value={'default'} disabled>
                                    {'請選擇分店：'}
                                </MenuItem>
                                {props.stores.map((store) => (
                                    <MenuItem value={store.storeId}>{store.storeName}</MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        <Grid container item flexDirection={'row'} justifyContent={'center'} md={3} xs={6}>
                            <Button
                                disabled={![props.selectedStore != 'default'].every((v) => v)}
                                className={props.classes.buttonCyan}
                                fullWidth
                                style={{ margin: 4, marginTop: 12 }}
                                onClick={() => {
                                    const lastEstimateTime =
                                        props.carQueues.length > 0
                                            ? Math.max(...props.carQueues.map((carQueue) => new Date(carQueue.estimateTime!).getTime())) +
                                              washMicroSeconds
                                            : new Date(moment().format('YYYY-MM-DDTHH:mm')).getTime();
                                    props.editCarQueueDialog.setInputCarQueueId(undefined);
                                    props.editCarQueueDialog.setStoreId(undefined);
                                    props.editCarQueueDialog.setInputCarIds([]);
                                    props.editCarQueueDialog.setStoreId(props.selectedStore);
                                    props.editCarQueueDialog.setInputMaxCarNumber(9);
                                    props.editCarQueueDialog.setInputEstimateTime(new Date(lastEstimateTime));
                                    props.editCarQueueDialog.setEditMode('line');
                                    props.editCarQueueDialog.onOpen();
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} fixedWidth />
                                {'新增車輛'}
                            </Button>
                        </Grid>
                        <Grid container item flexDirection={'row'} justifyContent={'center'} md={3} xs={6}>
                            <TextField
                                value={props.inputSearch}
                                onChange={(e) => props.setInputSearch(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                        {`搜尋(${['車牌號碼', '預估時間'].join(', ')})`}
                                    </>
                                }
                                style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                            />
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        item
                        flex={1}
                        padding={props.screenSize.mdUp ? 2 : 1}
                        borderRadius={2}
                        marginTop={2}
                        overflow={'hidden'}
                        sx={{ height: '100%', backgroundColor: 'white' }}
                    >
                        <Typography
                            component={'div'}
                            flex={1}
                            borderRadius={2}
                            sx={{
                                display: 'grid',
                                placeContent: 'start',
                                gridTemplateColumns: [
                                    '60px',
                                    '60px',
                                    '60px',
                                    '60px',
                                    '60px',
                                    '60px',
                                    'minmax(90px, 3fr)',
                                    'minmax(180px, 6fr)',
                                    '400px',
                                ].join(' '),
                                gap: 0,
                                borderCollapse: 'collapse',
                                overflow: 'auto',
                                maxHeight: '100%',
                                backgroundColor: 'white',
                                border: '1px solid rgba(224, 224, 224, 1)',
                                '& > div:nth-child(-n + 8)': {
                                    position: 'relative',
                                },
                                '& > div:nth-child(-n + 8)::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '33%',
                                    height: '33%',
                                    width: 0,
                                    borderRight: '2px solid rgba(0, 0, 0, 0.1)',
                                    right: 0,
                                },
                                // '& > div:nth-last-child(-n + 9)': { border: 0 },
                            }}
                        >
                            <Typography component={'div'} sx={{ ...rowStyle }}>
                                <Checkbox onChange={(e) => props.setRows(props.rows.map((r) => ({ ...r, selected: e.target.checked })))} />
                            </Typography>
                            {['檢查', '上移', '下移', '插前', '插後', '車牌號碼', '預估時間', '操作'].map((v) => (
                                <Typography key={v} component={'div'} sx={{ ...rowStyle }}>
                                    {v}
                                </Typography>
                            ))}
                            {props.filteredRows.map((row) => {
                                return (
                                    <Fragment key={row.carQueueId!}>
                                        <Typography
                                            aria-label={'勾選'}
                                            component={'div'}
                                            sx={{ ...rowStyle, backgroundColor: row.index % 2 == 0 ? grey[100] : 'white' }}
                                        >
                                            <Checkbox
                                                checked={row.selected}
                                                onClick={(e) => {
                                                    const shiftKey = e.nativeEvent.shiftKey;
                                                    if (!shiftKey) {
                                                        props.setRows([
                                                            ...props.rows.map((r) =>
                                                                r.carQueueId == row.carQueueId
                                                                    ? { ...r, selected: !row.selected }
                                                                    : { ...r },
                                                            ),
                                                        ]);
                                                        props.setPrevSelectedIndex(row.index);
                                                    } else if (shiftKey && props.prevSelectedIndex >= 0) {
                                                        const minIndex = Math.min(row.index, props.prevSelectedIndex);
                                                        const maxIndex = Math.max(row.index, props.prevSelectedIndex);
                                                        props.setRows([
                                                            ...props.rows.map((r) =>
                                                                r.index >= minIndex && r.index <= maxIndex
                                                                    ? { ...r, selected: !row.selected }
                                                                    : { ...r },
                                                            ),
                                                        ]);
                                                    }
                                                }}
                                            />
                                        </Typography>
                                        <Typography
                                            aria-label={'檢查'}
                                            component={'div'}
                                            sx={{ ...rowStyle, backgroundColor: row.index % 2 == 0 ? grey[100] : 'white' }}
                                        >
                                            {row.warnings.length == 0 ? (
                                                <Tooltip title={'檢查通過'} enterTouchDelay={0} leaveTouchDelay={3000}>
                                                    <div
                                                        style={{
                                                            color: 'white',
                                                            backgroundColor: green[500],
                                                            width: 24,
                                                            borderRadius: 12,
                                                        }}
                                                    >
                                                        <FontAwesomeIcon fixedWidth icon={faCheck} />
                                                    </div>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip
                                                    title={
                                                        <>
                                                            {row.warnings.map((msg) => (
                                                                <div>{msg}</div>
                                                            ))}
                                                        </>
                                                    }
                                                    enterTouchDelay={0}
                                                    leaveTouchDelay={3000}
                                                >
                                                    <div
                                                        style={{
                                                            color: 'white',
                                                            backgroundColor: orange[500],
                                                            width: 24,
                                                            borderRadius: 12,
                                                        }}
                                                    >
                                                        <FontAwesomeIcon fixedWidth icon={faExclamation} />
                                                    </div>
                                                </Tooltip>
                                            )}
                                        </Typography>
                                        <Typography
                                            aria-label={'上移'}
                                            component={'div'}
                                            sx={{ ...rowStyle, backgroundColor: row.index % 2 == 0 ? grey[100] : 'white' }}
                                        >
                                            <Button
                                                disabled={!row.prevCarQueue}
                                                className={props.classes.buttonCyan}
                                                sx={{ minWidth: 'unset !important' }}
                                                tabIndex={-1}
                                                onClick={async () => {
                                                    const currentData = await api.carQueue
                                                        .get({
                                                            carQueueIdIn: [row.carQueueId!, row.prevCarQueue!.carQueueId!],
                                                            store: { storeId: props.selectedStore },
                                                        })
                                                        .then((ret) => ret.data);
                                                    if (
                                                        currentData?.find(
                                                            (d) => d.carQueueId == row.carQueueId && d.estimateTime == row.estimateTime,
                                                        ) != undefined &&
                                                        currentData?.find(
                                                            (d) =>
                                                                d.carQueueId == row.prevCarQueue!.carQueueId &&
                                                                d.estimateTime == row.prevCarQueue!.estimateTime,
                                                        ) != undefined
                                                    ) {
                                                        await api.carQueue.updateBulk([
                                                            {
                                                                ...row,
                                                                carQueueId: row.carQueueId!,
                                                                estimateTime: row.prevCarQueue!.estimateTime,
                                                            },
                                                            {
                                                                ...row.prevCarQueue!,
                                                                carQueueId: row.prevCarQueue!.carQueueId!,
                                                                estimateTime: row.estimateTime,
                                                            },
                                                        ]);
                                                        props.page.simpleAlert.show({
                                                            autoHideDuration: 2000,
                                                            severity: 'success',
                                                            message: '資料更新成功',
                                                        });
                                                    } else {
                                                        props.page.simpleAlert.show({
                                                            autoHideDuration: 2000,
                                                            severity: 'warning',
                                                            message: '資料衝突，已更新',
                                                        });
                                                    }
                                                    props.setCarQueues(
                                                        await api.carQueue
                                                            .get({
                                                                complete: 'N',
                                                                cancel: 'N',
                                                                estimateTimeSt: new Date(new Date(moment().format('YYYY/MM/DD')).getTime()),
                                                                estimateTimeEd: new Date(
                                                                    new Date(moment().format('YYYY/MM/DD')).getTime() + 24 * 60 * 60 * 1000,
                                                                ),
                                                                store: { storeId: props.selectedStore },
                                                                order: { estimateTime: 'asc' },
                                                            })
                                                            .then((ret) => ret.data || []),
                                                    );
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faArrowUp} fixedWidth />
                                            </Button>
                                        </Typography>
                                        <Typography
                                            aria-label={'下移'}
                                            component={'div'}
                                            sx={{ ...rowStyle, backgroundColor: row.index % 2 == 0 ? grey[100] : 'white' }}
                                        >
                                            <Button
                                                disabled={!row.nextCarQueue}
                                                className={props.classes.buttonCyan}
                                                sx={{ minWidth: 'unset !important' }}
                                                tabIndex={-1}
                                                onClick={async () => {
                                                    const currentData = await api.carQueue
                                                        .get({
                                                            carQueueIdIn: [row.carQueueId!, row.nextCarQueue!.carQueueId!],
                                                            store: { storeId: props.selectedStore },
                                                        })
                                                        .then((ret) => ret.data);
                                                    if (
                                                        currentData?.find(
                                                            (d) => d.carQueueId == row.carQueueId && d.estimateTime == row.estimateTime,
                                                        ) != undefined &&
                                                        currentData?.find(
                                                            (d) =>
                                                                d.carQueueId == row.nextCarQueue!.carQueueId &&
                                                                d.estimateTime == row.nextCarQueue!.estimateTime,
                                                        ) != undefined
                                                    ) {
                                                        await api.carQueue.updateBulk([
                                                            {
                                                                ...row,
                                                                carQueueId: row.carQueueId!,
                                                                estimateTime: row.nextCarQueue!.estimateTime,
                                                            },
                                                            {
                                                                ...row.nextCarQueue!,
                                                                carQueueId: row.nextCarQueue!.carQueueId!,
                                                                estimateTime: row.estimateTime,
                                                            },
                                                        ]);
                                                        props.page.simpleAlert.show({
                                                            autoHideDuration: 2000,
                                                            severity: 'success',
                                                            message: '資料更新成功',
                                                        });
                                                    } else {
                                                        props.page.simpleAlert.show({
                                                            autoHideDuration: 2000,
                                                            severity: 'warning',
                                                            message: '資料衝突，已更新',
                                                        });
                                                    }
                                                    props.setCarQueues(
                                                        await api.carQueue
                                                            .get({
                                                                complete: 'N',
                                                                cancel: 'N',
                                                                estimateTimeSt: new Date(new Date(moment().format('YYYY/MM/DD')).getTime()),
                                                                estimateTimeEd: new Date(
                                                                    new Date(moment().format('YYYY/MM/DD')).getTime() + 24 * 60 * 60 * 1000,
                                                                ),
                                                                store: { storeId: props.selectedStore },
                                                                order: { estimateTime: 'asc' },
                                                            })
                                                            .then((ret) => ret.data || []),
                                                    );
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faArrowDown} fixedWidth />
                                            </Button>
                                        </Typography>
                                        <Typography
                                            aria-label={'插前'}
                                            component={'div'}
                                            sx={{ ...rowStyle, backgroundColor: row.index % 2 == 0 ? grey[100] : 'white' }}
                                        >
                                            <Button
                                                disabled={![row.prevSpaces > 0].every((v) => v)}
                                                className={props.classes.buttonCyan}
                                                sx={{ position: 'relative', minWidth: 'unset !important' }}
                                                tabIndex={-1}
                                                onClick={async () => {
                                                    props.editCarQueueDialog.setInputCarQueueId(undefined);
                                                    props.editCarQueueDialog.setStoreId(undefined);
                                                    props.editCarQueueDialog.setInputCarIds([]);
                                                    props.editCarQueueDialog.setInputMaxCarNumber(row.prevSpaces);
                                                    props.editCarQueueDialog.setInputEstimateTime(
                                                        new Date(row.estimateUnixTime - washMicroSeconds),
                                                    );
                                                    props.editCarQueueDialog.setEditMode('jump');
                                                    props.editCarQueueDialog.onOpen();
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faUpRight} fixedWidth />
                                                {row.prevSpaces > 0 && <div style={hintStyle}>{row.prevSpaces}</div>}
                                            </Button>
                                        </Typography>
                                        <Typography
                                            aria-label={'插後'}
                                            component={'div'}
                                            sx={{ ...rowStyle, backgroundColor: row.index % 2 == 0 ? grey[100] : 'white' }}
                                        >
                                            <Button
                                                disabled={![row.nextSpaces > 0].every((v) => v)}
                                                className={props.classes.buttonCyan}
                                                sx={{ position: 'relative', minWidth: 'unset !important' }}
                                                tabIndex={-1}
                                                onClick={async () => {
                                                    props.editCarQueueDialog.setInputCarQueueId(undefined);
                                                    props.editCarQueueDialog.setStoreId(undefined);
                                                    props.editCarQueueDialog.setInputCarIds([]);
                                                    props.editCarQueueDialog.setInputMaxCarNumber(row.nextSpaces);
                                                    props.editCarQueueDialog.setInputEstimateTime(
                                                        new Date(row.estimateUnixTime + washMicroSeconds),
                                                    );
                                                    props.editCarQueueDialog.setEditMode('line');
                                                    props.editCarQueueDialog.onOpen();
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTurnDownRight} fixedWidth />
                                                {row.nextSpaces > 0 && <div style={hintStyle}>{row.nextSpaces}</div>}
                                            </Button>
                                        </Typography>
                                        <Typography
                                            aria-label={'車牌號碼'}
                                            component={'div'}
                                            sx={{ ...rowStyle, backgroundColor: row.index % 2 == 0 ? grey[100] : 'white' }}
                                        >
                                            {row.carId}
                                        </Typography>
                                        <Typography
                                            aria-label={'預估時間'}
                                            component={'div'}
                                            sx={{ ...rowStyle, backgroundColor: row.index % 2 == 0 ? grey[100] : 'white' }}
                                        >
                                            {row.estimateTime ? moment(row.estimateTime).format('YYYY-MM-DD A hh:mm') : ''}
                                        </Typography>
                                        <Typography
                                            aria-label={'操作'}
                                            component={'div'}
                                            sx={{ ...rowStyle, backgroundColor: row.index % 2 == 0 ? grey[100] : 'white' }}
                                        >
                                            <Grid container justifyContent={'space-around'}>
                                                <Button
                                                    disabled={
                                                        ![row.users.filter((u) => (u.lineId || '').length > 0).length > 0].every((v) => v)
                                                    }
                                                    variant='contained'
                                                    className={props.classes.buttonBlue}
                                                    onClick={() =>
                                                        props.page.alert.show({
                                                            type: 'warning',
                                                            title: '警告',
                                                            message: '確定要通知？',
                                                            onConfirm: async () => {
                                                                props.page.setIsLoading(true);
                                                                const ret = await api.carQueue
                                                                    .inform({
                                                                        carQueueId: row.carQueueId!,
                                                                    })
                                                                    .finally(() => props.page.setIsLoading(false));
                                                                if (ret.success) {
                                                                    props.reloadCarQueues();
                                                                    props.page.simpleAlert.show({
                                                                        autoHideDuration: 2000,
                                                                        severity: 'success',
                                                                        message: ret.message,
                                                                    });
                                                                } else {
                                                                    props.page.alert.show({
                                                                        type: 'error',
                                                                        title: '錯誤',
                                                                        message: ret.message,
                                                                    });
                                                                }
                                                            },
                                                        })
                                                    }
                                                >
                                                    {'通知'}
                                                </Button>
                                                <Button
                                                    variant='contained'
                                                    className={props.classes.buttonCyan}
                                                    onClick={() =>
                                                        props.page.alert.show({
                                                            type: 'warning',
                                                            title: '警告',
                                                            message: '確定要完成？',
                                                            onConfirm: async () => {
                                                                props.page.setIsLoading(true);
                                                                const ret = await api.carQueue
                                                                    .update({ carQueueId: row.carQueueId!, complete: 'Y' })
                                                                    .finally(() => props.page.setIsLoading(false));
                                                                if (ret.success) {
                                                                    props.reloadCarQueues();
                                                                    props.page.simpleAlert.show({
                                                                        autoHideDuration: 2000,
                                                                        severity: 'success',
                                                                        message: ret.message,
                                                                    });
                                                                } else {
                                                                    props.page.simpleAlert.show({
                                                                        autoHideDuration: 6000,
                                                                        severity: 'error',
                                                                        message: JSON.stringify(ret),
                                                                    });
                                                                }
                                                            },
                                                        })
                                                    }
                                                >
                                                    {'完成'}
                                                </Button>
                                                <Button
                                                    variant='contained'
                                                    className={props.classes.buttonOrange}
                                                    onClick={() =>
                                                        props.page.alert.show({
                                                            type: 'warning',
                                                            title: '警告',
                                                            message: '確定要取消？',
                                                            onConfirm: async () => {
                                                                props.page.setIsLoading(true);
                                                                const ret = await api.carQueue
                                                                    .update({ carQueueId: row.carQueueId!, cancel: 'Y' })
                                                                    .finally(() => props.page.setIsLoading(false));
                                                                if (ret.success) {
                                                                    props.reloadCarQueues();
                                                                    props.page.simpleAlert.show({
                                                                        autoHideDuration: 2000,
                                                                        severity: 'success',
                                                                        message: ret.message,
                                                                    });
                                                                } else {
                                                                    props.page.simpleAlert.show({
                                                                        autoHideDuration: 6000,
                                                                        severity: 'error',
                                                                        message: JSON.stringify(ret),
                                                                    });
                                                                }
                                                            },
                                                        })
                                                    }
                                                >
                                                    {'取消'}
                                                </Button>
                                                <Button
                                                    variant='contained'
                                                    className={props.classes.buttonWhite}
                                                    onClick={() => {
                                                        props.editCarQueueDialog.setInputCarQueueId(row.carQueueId || '');
                                                        props.editCarQueueDialog.setStoreId(row.store?.storeId || '');
                                                        props.editCarQueueDialog.setInputCarIds([row.carId || '']);
                                                        props.editCarQueueDialog.setInputMaxCarNumber(1);
                                                        props.editCarQueueDialog.setInputEstimateTime(row.estimateTime!);
                                                        props.editCarQueueDialog.setEditMode('update');
                                                        props.editCarQueueDialog.onOpen();
                                                    }}
                                                >
                                                    {'編輯'}
                                                </Button>
                                                <Button
                                                    variant='contained'
                                                    className={props.classes.buttonRed}
                                                    onClick={() =>
                                                        props.page.alert.show({
                                                            type: 'warning',
                                                            title: '警告',
                                                            message: '確定要刪除？',
                                                            onConfirm: async () => {
                                                                props.page.setIsLoading(true);
                                                                const ret = await api.carQueue
                                                                    .remove({ carQueueId: row.carQueueId! })
                                                                    .finally(() => props.page.setIsLoading(false));
                                                                if (ret.success) {
                                                                    props.reloadCarQueues();
                                                                    props.page.simpleAlert.show({
                                                                        autoHideDuration: 2000,
                                                                        severity: 'success',
                                                                        message: ret.message,
                                                                    });
                                                                } else {
                                                                    props.page.simpleAlert.show({
                                                                        autoHideDuration: 6000,
                                                                        severity: 'error',
                                                                        message: JSON.stringify(ret),
                                                                    });
                                                                }
                                                            },
                                                        })
                                                    }
                                                >
                                                    {'刪除'}
                                                </Button>
                                            </Grid>
                                        </Typography>
                                    </Fragment>
                                );
                            })}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <EditCarQueueDialog {...props.editCarQueueDialog} />
            <AdvanceDialog {...props.advanceDialog} />
            <PostponeDialog {...props.postponeDialog} />
        </Page>
    );
};
